import appconfig from '@/appconfig'
import logger from '@/logger'
import { getContext, getDefaulContext } from './context'
import type { Intent } from '@/types'

// Message type definitions
interface BaseMessage {
  recipient: string;
  time: number;
  context: string[];
  version: string;
  sender?: string;
  options?: Record<string, unknown>;
  start?: boolean;
}

// Final merged type
export type BackendMessage = BaseMessage & {
  message?: ({ text: string } | Intent) & {sender?: string, recipient?: string};
  requestHistory?: boolean;
  command?: string;
  tempId?: string;
  api?: boolean;
};

class Message implements BackendMessage {
  recipient: string;
  time: number;
  context: string[];
  version: string;
  message?: ({ text: string } | Intent) & {sender?: string, recipient?: string};
  sender?: string;
  options?: Record<string, unknown>;
  start?: boolean;
  requestHistory?: boolean;
  command?: string;
  tempId?: string;
  api?: boolean;
  constructor(builder: MessageBuilderState) {
    this.recipient = builder._recipient
    this.time = builder._timestamp
    this.context = [`widget_url('${getDefaulContext()}')`].concat(getContext())

    // we always attach the widget widget version to the message
    this.version = appconfig.appVersion

    if (builder._message) {
      this.message = { text: builder._message }
    }


    if (builder._intent) {
      if (this.message) {
        logger.warn('Message has both intent and message. Message is overwritten by intent.')
      }
      this.message = builder._intent
    }

    // in case of an first message with a fresh session we need have no sender
    if (builder._sender) {
      this.sender = builder._sender
    }

    // I'm not sure why we need this or if we need this object at all
    if (builder._isInitialMessage) {
      this.options = {}
    }

    // if we have a initial message we and no older session we need to set start to true
    if (builder._isInitialMessage && !builder._sender) {
      this.start = true
    }

    if (builder._requestHistory) {
      this.requestHistory = true
    }

    if (builder._command) {
      this.command = builder._command
    }

    if (builder._uuid) {
      this.tempId = builder._uuid
    }

    if (builder._isApiCall) {
      this.api = true
      // we only get rid of the text if we have a message, all other fields need to be there - I think
      this.message = Object.assign({}, this.message)

      /**
       * Sender in root is for auth and will reject if not present,
       * but sender in message is to know who sent the message.
       * They are used at different steps in the moin Bot service
       */
      this.message.sender = this.sender;
      /**
       * Sender may be required to correctly store data in DB(?)
       */
      this.message.recipient = this.recipient;

      // make sure we don't send any text
      //this.message.text = undefined
      // we need better ws message handling and documentation
      // @ts-expect-error - we need to delete the text field I Think but Ts does not like it
      delete this.message.text
    }
  }
}

interface MessageBuilderState {
  _sender?: string;
  _recipient: string;
  _timestamp: number;
  _isInitialMessage: boolean;
  _isApiCall: boolean;
  _command: string;
  _message: string;
  _intent: Intent;
  _uuid: string;
  _context: string[];
  _requestHistory: boolean;
}


export class MessageBuilder implements MessageBuilderState {
  _sender?: string
  _recipient: string
  _timestamp: number
  _isInitialMessage!: boolean
  _isApiCall!: boolean
  _command!: string
  _message!: string
  _intent!: Intent
  _uuid!: string
  _context!: string[]
  _requestHistory!: boolean

  constructor(recipient: string) {
    this._recipient = recipient
    this._timestamp = Date.now()
  }

  setSender(sender?: string) {
    this._sender = sender
    return this
  }

  isInitialMessage() {
    this._isInitialMessage = true
    return this
  }

  setIsInitialMessage(value = false) {
    this._isInitialMessage = value
    return this
  }

  setIsApiCall(isApiCall = false) {
    this._isApiCall = isApiCall
    return this
  }

  overrideTimestamp(timestamp: number) {
    this._timestamp = timestamp
    return this
  }

  setCommand(command: string) {
    this._command = command
    return this
  }

  addMessage(message: string) {
    this._message = message
    return this
  }

  addIntent(intent?: Intent) {
    if (!intent) return this
    if (!intent.intent.name || !intent.intent.parameters) {
      return this
    }
    this._intent = {
      text: intent.text,
      intent: {
        name: intent.intent.name,
        parameters: intent.intent.parameters
      }
    }
    return this
  }

  addUUID(uuid: string) {
    this._uuid = uuid
    return this
  }

  addContext(context: string[]) {
    // THIS should come from context module?
    this._context = context
    return this
  }

  requestHistory() {
    this._requestHistory = true
    return this
  }

  setRequestHistory(value = false) {
    this._requestHistory = value
    return this
  }

  build() {
    // make sure it's an object and not a class
    const message = Object.assign({}, new Message(this))
    /* logger.log('message builder', message) */
    return message
  }
}
